import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectAdminGuardData } from '../store/auth/auth.selectors';
import { take, filter, switchMap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { of } from 'rxjs';
import { userNeedsProfileCompletion } from '../utils/user-needs-profile-completion';

/**
 * Guard Autenticazione - Verifica che l'utente sia loggato
 */
export const authGuard: CanActivateFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return of(true);
  }

  return store.select(selectAdminGuardData).pipe(
    // Aspetta che Firebase sia inizializzato E che il caricamento sia completo
    filter(authData => authData.isInitialized && !authData.isLoading),
    take(1),
    switchMap(authData => {
      if (!authData.isAuthenticated || !authData.user) {
        router.navigate(['/login']);
        return of(false);
      }
      const path = state.url.split('?')[0];
      if (
        userNeedsProfileCompletion(authData.user) &&
        !path.startsWith('/completa-registrazione')
      ) {
        router.navigate(['/completa-registrazione']);
        return of(false);
      }
      return of(true);
    })
  )
};

/**
 * Guard Guest - Blocca l'accesso a pagine guest se già autenticato
 */
export const guestGuard: CanActivateFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return of(true);
  }

  return store.select(selectAdminGuardData).pipe(
    // Aspetta che Firebase sia inizializzato E che il caricamento sia completo
    filter(authData => authData.isInitialized && !authData.isLoading),
    take(1),
    switchMap(authData => {
      if (authData.isAuthenticated) {
        if (authData.user && userNeedsProfileCompletion(authData.user)) {
          router.navigate(['/completa-registrazione']);
        } else {
          router.navigate(['/']);
        }
        return of(false);
      }
      return of(true);
    })
  )
};

/**
 * Se l'utente è loggato ma deve completare il profilo Google, reindirizza a /completa-registrazione.
 * Usare sulle route pubbliche (senza authGuard).
 */
export const pendingProfileGuard: CanActivateFn = (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return of(true);
  }

  return store.select(selectAdminGuardData).pipe(
    filter(authData => authData.isInitialized && !authData.isLoading),
    take(1),
    switchMap(authData => {
      if (!authData.user) {
        return of(true);
      }
      if (!userNeedsProfileCompletion(authData.user)) {
        return of(true);
      }
      const path = state.url.split('?')[0];
      if (path.startsWith('/completa-registrazione')) {
        return of(true);
      }
      router.navigate(['/completa-registrazione']);
      return of(false);
    }),
  );
};

/**
 * Pagina completamento profilo: solo utenti autenticati con profilo incompleto.
 */
export const completeRegistrationPageGuard: CanActivateFn = () => {
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store);
  const router = inject(Router);

  if (!isPlatformBrowser(platformId)) {
    return of(true);
  }

  return store.select(selectAdminGuardData).pipe(
    filter(authData => authData.isInitialized && !authData.isLoading),
    take(1),
    switchMap(authData => {
      if (!authData.isAuthenticated || !authData.user) {
        router.navigate(['/login']);
        return of(false);
      }
      if (!userNeedsProfileCompletion(authData.user)) {
        router.navigate(['/']);
        return of(false);
      }
      return of(true);
    }),
  );
};
